import { LinearGradient } from 'expo-linear-gradient'
import { observer } from 'mobx-react-lite'
import { Image, StyleSheet, TouchableOpacity, useWindowDimensions } from 'react-native'

import Banner from '../../../assets/MyFirstGameHome.png'
import { DS } from '../../../design-system'
import { useStore } from '../../../models'
import { useRouter } from 'expo-router'
import { defaultBreakPoints, isWeb, useBreakPointValue } from '@/components/WebWrapper'
import { ComponentProps, useMemo } from 'react'
import { useThrottledCallback } from 'use-debounce'
import { isMaintenanceModeEnabled, showMaintenanceModeAlert } from '@/screens/play-screen/maintenanceMode'

export const MyFirstGameWelcome = observer(({ compacted: compactedProp }: { compacted?: boolean }) => {
  const router = useRouter()
  const {
    analytics: { setEvent },
    session: { userInfo }
  } = useStore()

  const goToMyFirstGame = () => {
    if (isMaintenanceModeEnabled()) {
      showMaintenanceModeAlert()
      return
    }
    setEvent('First Game Started', { from_source: 'play_tab' })
    router.navigate('/game/my-first-game')
  }

  type CardConfig = Pick<MyFirstGameWelcomeCardProps, 'title' | 'description' | 'compacted'>
  const cardConfigDefault: CardConfig = {
    title: 'Play Your First Game',
    description: 'Guided play-through',
    compacted: compactedProp ?? isWeb
  }
  const cardConfigVariant: CardConfig = {
    title: 'Play First Game again',
    description: 'Guided play-through',
    compacted: compactedProp ?? true
  }

  const cardConfig = userInfo?.myFirstGameFinish === true ? cardConfigVariant : cardConfigDefault

  return (
    <MyFirstGameWelcomeCard
      title={cardConfig.title}
      description={cardConfig.description}
      compacted={cardConfig.compacted}
      onPress={goToMyFirstGame}
    />
  )
})

type MyFirstGameWelcomeCardProps = {
  onPress: () => void
  title: string
  description: string
  compacted?: boolean
}
/**
 * @param {boolean} compacted toggle between to layouts. compacted=true is without image and smaller title
 */
const MyFirstGameWelcomeCard = ({ onPress, title, description, compacted = true }: MyFirstGameWelcomeCardProps) => {
  const onPressDebounced = useThrottledCallback(onPress, 250, { leading: true })

  const arrowSizeBreakPoint = useBreakPointValue<'small' | 'default'>(
    {
      default: 'small',
      xs: 'default'
    },
    {
      breakPoints: {
        ...defaultBreakPoints,
        xs: 375
      }
    }
  )

  const isSmallDevice = arrowSizeBreakPoint === 'small'
  const arrowSize = isSmallDevice ? arrowSizeStyle.small : arrowSizeStyle.default

  const titleComponentProps: ComponentProps<typeof DS.Text> = useMemo(() => {
    if (compacted) {
      return {
        variant: 'Heading 4/Medium',
        color: 'Global/White'
      }
    }
    return {
      variant: 'Heading 3/Medium',
      color: 'Global/White',
      style: {
        lineHeight: 30
      }
    }
  }, [compacted])

  return (
    <TouchableOpacity onPress={onPressDebounced} activeOpacity={0.75}>
      <LinearGradient
        start={{ x: 0, y: 0 }}
        end={{ x: 1, y: 1 }}
        colors={['#3E0B7F', '#AD0087', '#FA5A00']}
        locations={[0, 0.5, 1]}
        style={styles.gradient}
      >
        <DS.Frame style={styles.container}>
          <DS.Frame style={styles.textContainer}>
            <DS.Frame>
              <DS.Text {...titleComponentProps}>{title}</DS.Text>
              <DS.Text variant="Body Medium/Medium" color="Brand/Yellow" style={{ marginTop: 2 }}>
                {description}
              </DS.Text>
            </DS.Frame>
            <DS.Frame>
              <TouchableOpacity style={[styles.arrow, arrowSize.container]} onPress={onPressDebounced}>
                <DS.Icon name={'Arrow---right'} size={arrowSize.icon.width} color={'Global/Black'} />
              </TouchableOpacity>
            </DS.Frame>
          </DS.Frame>
          {!compacted && <Image source={Banner} style={styles.banner} />}
        </DS.Frame>
      </LinearGradient>
    </TouchableOpacity>
  )
}

const arrowSizeStyle = {
  default: StyleSheet.create({
    container: {
      width: 48,
      height: 48,
      padding: 12
    },
    icon: {
      width: 24
    }
  }),
  small: StyleSheet.create({
    container: {
      width: 36,
      height: 36,
      padding: 8
    },
    icon: {
      width: 18
    }
  })
}
const styles = StyleSheet.create({
  arrow: {
    marginLeft: 12,
    gap: 8,
    backgroundColor: 'white',
    justifyContent: 'center',
    alignItems: 'center',
    borderRadius: 96
  },
  gradient: { borderRadius: 12 },
  container: { borderRadius: 12, padding: 16 },
  textContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between'
  },
  banner: { width: '100%', borderRadius: 12, height: 96, marginTop: 12 }
})
